import { z } from 'zod';

/**
 * Signing events—the vendor-neutral SigningService lifecycle. Emitted
 * server-side through `ServerAnalytics` off the signing DOMAIN events (not
 * inside any vendor adapter), so every signing vendor is instrumented for free:
 * PandaDoc today, DocuSign later, with no new events to add on cutover. The
 * signing system rides every event as the `vendor` enum rather than splitting
 * into per-vendor events (one action, different vendor → one event with a
 * discriminator; see analytics-event-conventions §4).
 *
 * These are migration/observability events. They are intentionally CDP-only and
 * do NOT duplicate the existing conversion signals: the sale-document signing
 * conversion stays with the established purchase/action-item events
 * (`bp_purchase_complete`, `vehicle_purchase_agreement_signed`,
 * `vehicle_document_signed`), which fire off action-item completion regardless
 * of which signing path produced the signature.
 *
 * PII: CDP-only, and these carry only ids + bounded enums — no buyer PII.
 *
 * Schemas cover caller-provided properties only. Backend identity/attribution
 * fields are injected by `ServerAnalytics` per call and stay out of these
 * schemas.
 */
// The signing systems the service can route to (mirrors `SigningVendor`).
const vendor = z
    .enum(['pandadoc', 'docusign'])
    .describe('The signing system handling the request.');
// Domain context shared by every signing event. Present on every event so each
// one joins back to its signing request and its sale (convention §12).
const signingContext = {
    signingRequestId: z.string().describe('The SigningRequest id this event is about.'),
    vendor,
    // Open string by design: docType comes from docTypeForPage, whose default
    // passes the raw page name through, so the value set is not closed and a
    // strict enum would reject legitimate future doc types.
    docType: z
        .string()
        .optional()
        .describe("Domain document type being signed, e.g. 'purchase-agreement' | 'bill-of-sale'."),
    purchasedVehicleId: z
        .string()
        .optional()
        .describe('Purchased vehicle this signing is for, when the signing carries PV context.'),
    placedOrderId: z
        .string()
        .optional()
        .describe('Placed order this signing is for, when the signing carries order context.'),
};
const SIGNING_EVENTS = {
    // Named for the trigger, not the state: this fires when the vendor document
    // is successfully SENT for signing (after the adapter's requestSignature
    // succeeds), not at bare record creation.
    // Dedupe: once per distinct signing request. A repeat create for the same
    // live request reuses it and does not re-emit (the service reuse path returns
    // without reaching the emit).
    SIGNING_REQUEST_SENT: {
        canonical: 'signing_request_sent',
        surface: 'backend',
        description: 'A signing session was initiated: the vendor document was sent for signing through the SigningService.',
        schema: z.object({
            ...signingContext,
            signatureType: z
                .enum(['embedded', 'email-link'])
                .optional()
                .describe('How the signer accesses the document.'),
            // Open strings by design: party role is a documented open-ended set
            // ('buyer' | 'co-buyer' | 'dealer' | 'ekho' | 'lender' | 'notary' | …), so
            // a strict enum would reject legitimate future roles.
            partyRoles: z
                .array(z.string())
                .optional()
                .describe('Roles of the parties on the request, e.g. ["buyer"].'),
        }),
    },
    // Dedupe: once per party per request. The inbound processor emits only on a
    // genuine PENDING→SIGNED transition, so a redelivered webhook for an
    // already-signed party does not re-emit.
    SIGNING_PARTY_COMPLETED: {
        canonical: 'signing_party_completed',
        surface: 'backend',
        description: 'A single party finished signing a document in a signing request.',
        schema: z.object({
            ...signingContext,
            // Open string by design — see partyRoles above.
            role: z.string().optional().describe('Role of the party that signed, e.g. "buyer".'),
        }),
    },
    // Dedupe: once per request. Emitted only on the transition INTO COMPLETED, so
    // a redelivered completion webhook on an already-complete request does not
    // re-emit.
    SIGNING_COMPLETED: {
        canonical: 'signing_completed',
        surface: 'backend',
        description: 'Every required party has signed; the signing request is complete.',
        schema: z.object({
            ...signingContext,
            secondsToComplete: z
                .number()
                .optional()
                .describe('Wall-clock seconds from request creation to completion, when derivable.'),
        }),
    },
    // Dedupe: once per request (FAILED is terminal).
    SIGNING_FAILED: {
        canonical: 'signing_failed',
        surface: 'backend',
        description: 'A signing request reached a terminal failure before completion.',
        schema: z.object({
            ...signingContext,
            reason: z
                .enum(['vendor-initiation-error', 'id-verification', 'declined', 'voided'])
                .optional()
                .describe('Bounded machine reason code for the failure.'),
        }),
    },
    // Dedupe: once per request (REVOKED is terminal).
    SIGNING_REVOKED: {
        canonical: 'signing_revoked',
        surface: 'backend',
        description: 'An in-flight signing request was revoked/voided in the vendor system.',
        schema: z.object({
            ...signingContext,
            reason: z
                .enum(['revoked'])
                .optional()
                .describe('Bounded machine reason code for the revocation.'),
        }),
    },
};

export { SIGNING_EVENTS };
//# sourceMappingURL=signing.js.map
